import type { ComponentProps, CSSProperties, FC, ReactNode } from 'react';
import { cn } from '../lib/utils';

type BarListItem = { label: string; value: number };

type BarListProps = Omit<ComponentProps<'figure'>, 'children'> & {
  items: readonly BarListItem[];
  /** Text equivalent of the plot. Screen readers get this instead of the bars. */
  summary: ReactNode;
  /** Value at the full bar width. Defaults to the largest value. */
  max?: number;
  formatValue?: (value: number) => ReactNode;
  /** Bar colour, as a token reference such as `var(--z-brand)`. */
  color?: string;
};

/**
 * Horizontal bars, one per row: the label and its value share a line, with
 * the bar directly beneath on a faint track, so each value reads with its own
 * bar. Use it to compare a handful of named quantities.
 *
 * Bars grow from the left when they mount and ease to a new length when a
 * value changes (`--z-dur-slow`, emphasized). Reduced motion turns both off
 * through the global rule in styles.css.
 */
export const BarList: FC<BarListProps> = ({
  items,
  summary,
  max = Math.max(0, ...items.map((item) => item.value)),
  formatValue = (value) => value,
  color = 'var(--z-text-primary)',
  className,
  ...props
}) => (
  <figure className={cn('w-full', className)} {...props}>
    <figcaption className="sr-only">{summary}</figcaption>

    <ul aria-hidden="true" className="flex flex-col gap-4">
      {items.map((item) => {
        const share = max > 0 ? Math.min(1, Math.max(0, item.value / max)) : 0;

        return (
          <li key={item.label} className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate text-body-lg text-foreground">
                {item.label}
              </span>
              <span className="shrink-0 text-body-lg font-semibold text-foreground tabular-nums">
                {formatValue(item.value)}
              </span>
            </div>
            <div className="h-2.5 overflow-hidden rounded-xs bg-[var(--z-border-subtle)]">
              <div
                className="bar-list-bar h-full rounded-xs bg-(--bar-color) transition-[width] duration-[var(--z-dur-slow)] ease-[var(--z-ease-emphasized)]"
                style={
                  {
                    width: `${share * 100}%`,
                    '--bar-color': color
                  } as CSSProperties
                }
              />
            </div>
          </li>
        );
      })}
    </ul>
  </figure>
);
